import React, { useState, useEffect } from 'react';
import { Modal, ModalProps, Button, Space, List, Typography, Checkbox, message } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import './CategorySelectionModal.scss';
import { useCategoriesApi } from 'api/useCategories';

interface CategorySecond {
  id: number;
  name: string;
  category_prime_id: number;
  is_active: boolean;
}

interface CategoryPrime {
  id: number;
  name: string;
  category_root_id: number;
  is_active: boolean;
  category_second: CategorySecond[];
}

interface CategoryRoot {
  id: number;
  name: string;
  client_id: number;
  order_by: number;
  last_modified: string;
  category_prime: CategoryPrime[];
}

interface CategorySelectionModalProps extends ModalProps {
  onCancel: () => void;
  onAdd: (selectedCategories: any[]) => void;
  editingCategory?: any;
  profile_id: number | null;
}

export const CategorySelectionModal: React.FC<CategorySelectionModalProps> = ({
  onCancel,
  onAdd,
  editingCategory,
  profile_id,
  ...rest
}) => {
  const [categories, setCategories] = useState<CategoryRoot[]>([]);
  const [selectedMainCategory, setSelectedMainCategory] = useState<CategoryRoot | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<CategoryPrime | null>(null);
  const [selectedProductCategories, setSelectedProductCategories] = useState<string[]>([]);
  const { getAllCategories } = useCategoriesApi();

  useEffect(() => {
    // In edit mode the modal mounts with profile_id=null (deal not yet hydrated), then profile_id becomes the
    // deal's client id — firing two fetches. Guard against the stale null-profile response resolving last and
    // overwriting the deal-client categories.
    let ignore = false;
    const fetchCategories = async () => {
      try {
        const response = await getAllCategories(profile_id || undefined);
        if (!ignore) setCategories(response || []);
      } catch (error) {
        if (!ignore) console.error('Error fetching categories:', error);
      }
    };

    fetchCategories();
    return () => {
      ignore = true;
    };
  }, [profile_id]);

  useEffect(() => {
    if (!rest.open) {
      setSelectedMainCategory(null);
      setSelectedSubCategory(null);
      setSelectedProductCategories([]);
    } else if (editingCategory && categories.length > 0) {
      const mainCategory = categories.find((cat) => cat.name === editingCategory.mainCategory);
      if (mainCategory) {
        setSelectedMainCategory(mainCategory);

        const subCategory = mainCategory.category_prime?.find((sub) => sub.name === editingCategory.subCategory);
        if (subCategory) {
          setSelectedSubCategory(subCategory);
          setSelectedProductCategories(editingCategory.productCategories || []);
        }
      }
    }
  }, [editingCategory, categories, rest.open]);

  const handleMainCategorySelect = (category: CategoryRoot) => {
    setSelectedMainCategory(category);
    setSelectedSubCategory(null);
    setSelectedProductCategories([]);
  };

  const handleSubCategorySelect = (category: CategoryPrime) => {
    setSelectedSubCategory(category);
    setSelectedProductCategories([]);
  };

  const handleProductCategorySelect = (category: string, checked: boolean) => {
    setSelectedProductCategories((prev) => (checked ? [...prev, category] : prev.filter((c) => c !== category)));
  };

  const handleAdd = () => {
    if (selectedMainCategory) {
      const selectedProductCategoryIds = allProductCategories
        .filter((category) => selectedProductCategories.includes(category.name))
        .map((category) => category.id);

      if (selectedProductCategoryIds.length !== selectedProductCategories.length) {
        message.error('Some product categories could not be matched to IDs');
        return;
      }

      onAdd([
        {
          mainCategory: selectedMainCategory.name,
          subCategory: selectedSubCategory?.name,
          productCategories: selectedProductCategories,
          categoryRootId: selectedMainCategory.id,
          categoryPrimeId: selectedSubCategory?.id,
          categorySecondIds: selectedProductCategoryIds
        }
      ]);
    }
  };

  const getSubCategories = (): CategoryPrime[] => {
    return selectedMainCategory ? selectedMainCategory.category_prime || [] : [];
  };

  const getProductCategories = (): CategorySecond[] => {
    return selectedSubCategory ? selectedSubCategory.category_second || [] : [];
  };

  const allProductCategories = getProductCategories();

  return (
    <Modal
      open={rest.open}
      onCancel={onCancel}
      footer={null}
      width={800}
      destroyOnClose
      className="category-selection-modal"
    >
      <div className="flex gap-4 mb-3">
        <div className="flex-1">
          <Typography.Title level={5} className="mb-0">
            Select Main Category
          </Typography.Title>
        </div>
        <div className="flex-1">
          <Typography.Title level={5} className="mb-0">
            Select a Subcategory
          </Typography.Title>
        </div>
        <div className="flex-1">
          <Typography.Title level={5} className="mb-0">
            Select Product Categories
          </Typography.Title>
        </div>
      </div>
      <hr className="border-t border-gray-200 my-2" />

      <div className="flex gap-4 mb-6 min-w-0">
        <div className="flex-1 min-w-0">
          <List
            size="small"
            dataSource={categories}
            className="max-h-64 overflow-y-auto"
            renderItem={(category) => (
              <List.Item
                className={`cursor-pointer p-2 mb-1 relative ${
                  selectedMainCategory?.id === category.id
                    ? 'bg-[#152d51] text-white category-arrow'
                    : 'bg-gray-100 hover:bg-gray-100'
                }`}
                onClick={() => handleMainCategorySelect(category)}
              >
                <div className="truncate w-full" title={category.name}>
                  {category.name}
                </div>
              </List.Item>
            )}
          />
        </div>

        <div className="flex-1 min-w-0">
          {selectedMainCategory ? (
            <List
              size="small"
              dataSource={getSubCategories().sort((a, b) => a.name.localeCompare(b.name))}
              className="max-h-64 overflow-y-auto"
              renderItem={(category) => (
                <List.Item
                  className={`cursor-pointer p-2 rounded mb-2 relative ${
                    selectedSubCategory?.id === category.id
                      ? 'bg-[#152d51] text-white category-arrow'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                  onClick={() => handleSubCategorySelect(category)}
                >
                  <div className="truncate w-full" title={category.name}>
                    {category.name}
                  </div>
                </List.Item>
              )}
            />
          ) : (
            <div className="flex items-center justify-center text-center text-gray-500 h-20" style={{ height: '80%' }}>
              <span className="mt-4">Select a main category to view subcategory</span>
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0" style={{ border: '1px solid #e5e7eb' }}>
          {selectedSubCategory ? (
            <div className="flex flex-col gap-2 max-h-64 overflow-y-auto">
              <List
                size="small"
                dataSource={allProductCategories.sort((a, b) => a.name.localeCompare(b.name))}
                className="overflow-y-auto products-list"
                renderItem={(category) => (
                  <List.Item className="p-2 mb-1">
                    <Checkbox
                      checked={selectedProductCategories.includes(category.name)}
                      onChange={(e) => handleProductCategorySelect(category.name, e.target.checked)}
                    >
                      <div className="truncate w-full" title={category.name}>
                        {category.name}
                      </div>
                    </Checkbox>
                  </List.Item>
                )}
              />
            </div>
          ) : (
            <div
              className="flex items-center justify-center text-center text-gray-500"
              style={{ height: '80%', border: 'none' }}
            >
              Select a subcategory to view product categories
            </div>
          )}
        </div>
      </div>
      <hr className="border-t border-gray-200 my-2" />

      <div className="flex justify-end gap-2">
        <Button onClick={onCancel} className="w-24 h-10">
          Cancel
        </Button>
        <Button
          type="primary"
          icon={<ArrowRightOutlined />}
          onClick={handleAdd}
          disabled={!selectedMainCategory || !selectedSubCategory}
          className="w-28 h-10"
        >
          {editingCategory ? 'Update' : 'Add'}
        </Button>
      </div>
    </Modal>
  );
};
